import React from "react";
import { IoClose } from "react-icons/io5";
import { MdDeleteForever, MdRestore } from "react-icons/md";
import { FileIcon } from "./UploadBadges";

/**
 * Trash Bin modal — displays soft-deleted organizations and (per-org)
 * soft-deleted folders/files, with per-row restore & delete-forever controls.
 *
 * Purely presentational: the parent owns all data and mutations.
 */
const TrashBinModal = ({
  isOpen,
  onClose,
  selectedOrg,
  activeTab,
  setActiveTab,
  deletedOrgs,
  trashData,
  trashLoading,
  deleteOrgMutation,
  deleteForeverOrgMutation,
  deleteUploadMutation,
  deleteForeverUploadMutation,
  onRestoreOrg,
  onDeleteOrgForever,
  onRestoreUpload,
  onDeleteUploadForever,
}) => {
  if (!isOpen) return null;

  const isOrgsTab = activeTab === "orgs";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden animate-in zoom-in-95 duration-150 flex flex-col h-[500px]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Trash Bin</h3>
            {selectedOrg && (
              <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                Active Org:{" "}
                <span className="font-semibold text-indigo-600">
                  {selectedOrg.organizationsName}
                </span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <IoClose size={18} />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-100 bg-slate-50/50 shrink-0 px-4">
          <button
            onClick={() => setActiveTab("orgs")}
            className={`px-4 py-3 text-xs font-bold transition-all relative border-b-2 ${isOrgsTab
              ? "text-indigo-600 border-indigo-600"
              : "text-slate-500 border-transparent hover:text-slate-800"
              }`}
          >
            Organizations ({deletedOrgs.length})
          </button>
          <button
            onClick={() => setActiveTab("uploads")}
            className={`px-4 py-3 text-xs font-bold transition-all relative border-b-2 ${!isOrgsTab
              ? "text-indigo-600 border-indigo-600"
              : "text-slate-500 border-transparent hover:text-slate-800"
              }`}
          >
            Folders & Files ({selectedOrg ? trashData.length : 0})
          </button>
        </div>

        {/* Tab Contents */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {isOrgsTab ? (
            deletedOrgs.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-3xl">🗑️</span>
                <p className="text-xs text-slate-400 mt-2 font-medium">
                  No deleted organizations found
                </p>
              </div>
            ) : (
              deletedOrgs.map((org) => {
                const isRestoring =
                  deleteOrgMutation.isPending &&
                  deleteOrgMutation.variables?.id === org.organizationsDNSId &&
                  deleteOrgMutation.variables?.isDeleted === false;
                const isDeletingForever =
                  deleteForeverOrgMutation.isPending &&
                  deleteForeverOrgMutation.variables === org.organizationsDNSId;
                const rowBusy = isRestoring || isDeletingForever;

                return (
                  <div
                    key={org.organizationsDNSId}
                    className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-xl hover:bg-slate-100/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="inline-flex items-center justify-center bg-slate-200 text-slate-600 text-[9px] font-bold px-1.5 py-0.5 rounded tracking-wide shrink-0">
                        ORG
                      </span>
                      <div className="min-w-0">
                        <h4 className="text-xs font-bold text-slate-700 truncate">
                          {org.organizationsName}
                        </h4>
                        <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                          Deleted{" "}
                          {org.modifiedDate
                            ? new Date(org.modifiedDate).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })
                            : "recently"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        data-tooltip-id="toolbar-tooltip"
                        data-tooltip-content={isRestoring ? "Restoring..." : "Restore"}
                        onClick={() =>
                          onRestoreOrg(org.organizationsDNSId, org.organizationsName)
                        }
                        disabled={rowBusy}
                        className="flex items-center p-1.5 text-xs font-semibold bg-[#4F46E5] text-white rounded-full transition-colors disabled:opacity-50"
                      >
                        {isRestoring ? (
                          <span className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                        ) : (
                          <MdRestore size={15} className="text-white shrink-0" />
                        )}
                      </button>
                      <button
                        data-tooltip-id="toolbar-tooltip"
                        data-tooltip-content={isDeletingForever ? "Deleting..." : "Delete Forever"}
                        onClick={() =>
                          onDeleteOrgForever(org.organizationsDNSId, org.organizationsName)
                        }
                        disabled={rowBusy}
                        className="flex items-center p-1.5 text-xs font-semibold bg-red text-white rounded-full transition-colors disabled:opacity-50 "
                      >
                        {isDeletingForever ? (
                          <span className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                        ) : (
                          <MdDeleteForever size={15} className="text-white shrink-0" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })
            )
          ) : !selectedOrg ? (
            <div className="text-center py-12">
              <span className="text-3xl">🏢</span>
              <p className="text-xs text-slate-400 mt-2 font-medium">
                Please select an organization to view its deleted folders &amp; files
              </p>
            </div>
          ) : trashLoading ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-2">
              <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-slate-400 font-medium">Loading trash data...</span>
            </div>
          ) : trashData.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-3xl">📁</span>
              <p className="text-xs text-slate-400 mt-2 font-medium">
                No deleted folders or files found
              </p>
            </div>
          ) : (
            trashData.map((item) => {
              const itemId = item.uploadID ?? item.folderDNSID ?? item.id;
              const itemName = item.fileName ?? item.folderName ?? item.name ?? "Untitled";
              const rawType =
                item.fileType ?? item.type ?? (item.folderDNSID ? "Folder" : "File");
              const itemType = rawType === "1" ? "Folder" : rawType;

              const isRestoring =
                deleteUploadMutation.isPending &&
                deleteUploadMutation.variables?.payload?.[0]?.UploadIDs === String(itemId) &&
                deleteUploadMutation.variables?.payload?.[0]?.isDeleted === false;

              const isDeletingForever =
                deleteForeverUploadMutation.isPending &&
                deleteForeverUploadMutation.variables?.[0]?.uploadIDs === String(itemId);

              const rowBusy = isRestoring || isDeletingForever;

              return (
                <div
                  key={itemId}
                  className="flex items-center justify-between p-3.5 bg-slate-50 border border-slate-100 rounded-xl hover:bg-slate-100/50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileIcon type={itemType} size={15} />
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-slate-700 truncate">{itemName}</h4>
                      <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                        Type:{" "}
                        <span className="font-semibold text-slate-500">{itemType}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      data-tooltip-id="toolbar-tooltip"
                      data-tooltip-content={isRestoring ? "Restoring..." : "Restore"}
                      onClick={() => onRestoreUpload(itemId, itemName, itemType)}
                      disabled={rowBusy}
                      className="flex items-center p-1.5 text-xs font-semibold bg-[#4F46E5] text-white rounded-full transition-colors disabled:opacity-50"
                    >
                      {isRestoring ? (
                        <span className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                      ) : (
                        <MdRestore size={15} className="text-white shrink-0" />
                      )}
                    </button>
                    <button
                      data-tooltip-id="toolbar-tooltip"
                      data-tooltip-content={isDeletingForever ? "Deleting..." : "Delete Forever"}
                      onClick={() => onDeleteUploadForever(itemId, itemName, itemType)}
                      disabled={rowBusy}
                      className="flex items-center p-1.5 text-xs font-semibold bg-red text-white rounded-full transition-colors disabled:opacity-50"
                    >
                      {isDeletingForever ? (
                        <span className="w-2.5 h-2.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                      ) : (
                        <MdDeleteForever size={15} className="text-white shrink-0" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-all shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrashBinModal;
